import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Minus, Plus } from "lucide-react";

type RankRange = "all" | "-100" | "100-1000" | "1000-5000" | "5000-10000" | "+10K";
type FieldType = "all" | "SAY" | "EA" | "SÖZ" | "DİL";

interface CoachFiltersProps {
  universities: string[];
  selectedUniversities: string[];
  onUniversityChange: (universities: string[]) => void;
  selectedRankRange: RankRange;
  onRankRangeChange: (range: RankRange) => void;
  selectedField: FieldType;
  onFieldChange: (field: FieldType) => void;
  isUniversityExpanded: boolean;
  onUniversityExpandToggle: () => void;
  isRankExpanded: boolean;
  onRankExpandToggle: () => void;
  isFieldExpanded: boolean;
  onFieldExpandToggle: () => void;
  showFreeTrialOnly: boolean;
  onFreeTrialChange: (checked: boolean) => void;
}

export const CoachFilters = ({
  universities,
  selectedUniversities,
  onUniversityChange,
  selectedRankRange,
  onRankRangeChange,
  selectedField,
  onFieldChange,
  isUniversityExpanded,
  onUniversityExpandToggle,
  isRankExpanded,
  onRankExpandToggle,
  isFieldExpanded,
  onFieldExpandToggle,
  showFreeTrialOnly,
  onFreeTrialChange,
}: CoachFiltersProps) => {
  return (
    <div className="space-y-6">
      {/* Free Trial Filter */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="free-trial"
            checked={showFreeTrialOnly}
            onCheckedChange={(checked) => onFreeTrialChange(checked as boolean)}
          />
          <label htmlFor="free-trial" className="text-sm text-[#403E43]">
            Sadece Ücretsiz Görüşme
          </label>
        </div>
      </div>

      {/* Universities Filter */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#403E43]">Üniversiteler</h3>
          <button 
            onClick={onUniversityExpandToggle}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            {isUniversityExpanded ? (
              <Minus className="h-4 w-4 text-[#9b87f5]" />
            ) : (
              <Plus className="h-4 w-4 text-[#9b87f5]" />
            )}
          </button>
        </div>
        {isUniversityExpanded && (
          <div className="space-y-3 max-h-48 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#9b87f5] scrollbar-track-transparent">
            {universities.map((university) => (
              <div key={university} className="flex items-center space-x-2">
                <Checkbox
                  id={university}
                  checked={selectedUniversities.includes(university)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onUniversityChange([...selectedUniversities, university]);
                    } else {
                      onUniversityChange(selectedUniversities.filter((u) => u !== university));
                    }
                  }}
                />
                <label htmlFor={university} className="text-sm text-[#403E43]">
                  {university}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rank Range Filter */}
      <div className="space-y-4 border-t border-[#8E9196]/20 pt-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#403E43]">Sıralama Aralığı</h3>
          <button 
            onClick={onRankExpandToggle}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            {isRankExpanded ? (
              <Minus className="h-4 w-4 text-[#9b87f5]" />
            ) : (
              <Plus className="h-4 w-4 text-[#9b87f5]" />
            )}
          </button>
        </div>
        {isRankExpanded && (
          <RadioGroup 
            value={selectedRankRange} 
            onValueChange={onRankRangeChange}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all" id="all-ranks" />
              <Label htmlFor="all-ranks" className="text-sm text-[#403E43]">Tüm Sıralamalar</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="-100" id="top-100" />
              <Label htmlFor="top-100" className="text-sm text-[#403E43]">İlk 100</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="100-1000" id="100-1000" />
              <Label htmlFor="100-1000" className="text-sm text-[#403E43]">100 - 1.000</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="1000-5000" id="1000-5000" />
              <Label htmlFor="1000-5000" className="text-sm text-[#403E43]">1.000 - 5.000</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="5000-10000" id="5000-10000" />
              <Label htmlFor="5000-10000" className="text-sm text-[#403E43]">5.000 - 10.000</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="+10K" id="10K-plus" />
              <Label htmlFor="10K-plus" className="text-sm text-[#403E43]">10.000+</Label>
            </div>
          </RadioGroup>
        )}
      </div>

      {/* Field Filter */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#403E43]">Alan</h3>
          <button 
            onClick={onFieldExpandToggle}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            {isFieldExpanded ? (
              <Minus className="h-4 w-4 text-[#9b87f5]" />
            ) : (
              <Plus className="h-4 w-4 text-[#9b87f5]" />
            )}
          </button>
        </div>
        {isFieldExpanded && (
          <RadioGroup
            value={selectedField}
            onValueChange={onFieldChange}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all" id="all" />
              <Label htmlFor="all">Tümü</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="SAY" id="SAY" />
              <Label htmlFor="SAY">Sayısal</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="EA" id="EA" />
              <Label htmlFor="EA">Eşit Ağırlık</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="SÖZ" id="SÖZ" />
              <Label htmlFor="SÖZ">Sözel</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="DİL" id="DİL" />
              <Label htmlFor="DİL">Dil</Label>
            </div>
          </RadioGroup>
        )}
      </div>
    </div>
  );
};